import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api';
import { AuthGate, useAuthSession } from './auth/AuthProvider';
import { AdminPanel } from './components/AdminPanel';
import { BudgetRequestForm } from './components/BudgetRequestForm';
import { ClaudeCodeSetup } from './components/ClaudeCodeSetup';
import { CreateKeyForm } from './components/CreateKeyForm';
import { GuardrailChangeForm } from './components/GuardrailChangeForm';
import { GuardrailRequestForm } from './components/GuardrailRequestForm';
import { KeyList } from './components/KeyList';
import { ModelCatalog } from './components/ModelCatalog';
import { PlaintextModal } from './components/PlaintextModal';
import { StatusBar } from './components/StatusBar';
import type {
  BudgetRequestIn,
  CustomerFlagIn,
  GuardrailRequestIn,
  HealthOut,
  Identity,
  KeyCreateIn,
  KeyCreatedOut,
  KeyListOut,
  KeyGuardrailChangeIn,
  KeyGuardrailNameOut,
  KeyOut,
  KeyUpdateIn,
  MyModelsOut,
  TeamSummary,
} from './types';

interface Data {
  health: HealthOut | null;
  me: Identity | null;
  teams: TeamSummary[] | null;
  keys: KeyListOut;
}

/**
 * The gate wraps the view rather than sitting in main.tsx so that mounting <App /> — in
 * tests as well as in the browser — always goes through the real boot sequence: read
 * /api/auth-config, then decide whether a sign-in is required.
 */
export default function App() {
  return (
    <AuthGate>
      <KeyView />
    </AuthGate>
  );
}

function KeyView() {
  const session = useAuthSession();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<KeyCreatedOut | null>(null);
  const [mutationWarning, setMutationWarning] = useState<string | null>(null);
  const [requestIncreaseKey, setRequestIncreaseKey] = useState<KeyOut | null>(null);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestGuardrailKey, setRequestGuardrailKey] = useState<KeyOut | null>(null);
  const [setupKey, setSetupKey] = useState<KeyOut | null>(null);
  const [changeGuardrailKey, setChangeGuardrailKey] = useState<KeyOut | null>(null);
  const [guardrailChangeSubmitting, setGuardrailChangeSubmitting] = useState(false);
  const [guardrailRequestSubmitting, setGuardrailRequestSubmitting] = useState(false);
  // Assigned-guardrail names for the key table, loaded separately from the key list so a
  // slow account-wide assignment scan never blocks the table. null = not yet loaded (show
  // a placeholder); a Map = loaded (a missing key id means "no guardrail").
  const [guardrailNames, setGuardrailNames] = useState<Map<string, KeyGuardrailNameOut> | null>(
    null,
  );
  const [models, setModels] = useState<MyModelsOut | 'error' | null>(null);
  // Generation counter, not an AbortController: a superseded or unmounted load must
  // not write state, but these GETs are cheap and idempotent so cancelling adds nothing.
  const generation = useRef(0);

  const load = useCallback(async (refresh = false) => {
    const gen = ++generation.current;
    const current = () => gen === generation.current;
    setLoading(true);
    setError(null);
    setMutationWarning(null);
    try {
      // The key list is the point of this view, so it alone is fatal. Health, identity
      // and teams are chrome: degrade to "unknown" rather than blanking the page.
      const [keys, health, me, teams] = await Promise.all([
        api.keys(refresh),
        api.health().catch(() => null),
        api.me().catch(() => null),
        api.teams().catch(() => null),
      ]);
      if (!current()) return;
      setData({ keys, health, me, teams });
      // Guardrail names load separately and non-fatally: reset to the placeholder state
      // here, then fill in once the (potentially slow) assignment scan returns. A failure
      // or unavailable lookup leaves an empty map, so the column reads "none" quietly.
      setGuardrailNames(null);
      void api
        .keyGuardrailNames()
        .then((res) => {
          if (!current()) return;
          setGuardrailNames(
            res.available ? new Map(res.items.map((i) => [i.key_id, i])) : new Map(),
          );
        })
        .catch(() => {
          if (current()) setGuardrailNames(new Map());
        });
      setModels(null);
      void api
        .myModels()
        .then((res) => {
          if (current()) setModels(res);
        })
        .catch(() => {
          if (current()) setModels('error');
        });
    } catch (e) {
      if (!current()) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setData(null);
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  const handleCreateKey = async (input: KeyCreateIn) => {
    try {
      const result = await api.createKey(input);
      setCreatedKey(result);
      setShowCreateForm(false);
      // Refresh the list to show the new key
      await load();
    } catch (err) {
      // Re-throw so the form can display the error
      throw err;
    }
  };

  const handleUpdateKey = async (id: string, input: KeyUpdateIn) => {
    try {
      const result = await api.updateKey(id, input);
      if (result.warning) {
        setMutationWarning(result.warning);
      }
      // Refresh the list to show server truth
      await load();
    } catch (err) {
      // Handle specific error codes
      if (err instanceof ApiError) {
        if (err.code === 'limit_increase_forbidden') {
          throw new Error(
            'Limit increases are not permitted directly. Use "Request increase" to request a temporary budget increase.',
          );
        }
        if (err.code === 'over_team_budget') {
          throw new Error(
            "This limit would push the team's key limits over its budget. Raise the team budget in the admin Teams tab first.",
          );
        }
        if (err.code === 'upstream_error') {
          throw new Error(
            'OpenRouter is busy and the change was not applied. This is an upstream fault. Please try again.',
          );
        }
      }
      throw err;
    }
  };

  const handleDisableKey = async (id: string) => {
    try {
      const result = await api.disableKey(id);
      if (result.warning) {
        setMutationWarning(result.warning);
      }
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'upstream_error') {
        throw new Error(
          'OpenRouter is busy and the key was not disabled. This is an upstream fault. Please try again.',
        );
      }
      throw err;
    }
  };

  const handleSetCustomer = async (id: string, input: CustomerFlagIn) => {
    try {
      const result = await api.setCustomerKey(id, input);
      if (result.warning) {
        setMutationWarning(result.warning);
      }
      await load();
    } catch (err) {
      throw err;
    }
  };

  const handleChangeGuardrail = async (input: KeyGuardrailChangeIn) => {
    if (!changeGuardrailKey) return;
    setGuardrailChangeSubmitting(true);
    try {
      const row = await api.changeKeyGuardrail(changeGuardrailKey.id, input);
      setChangeGuardrailKey(null);
      setMutationWarning(
        row.status === 'active'
          ? null
          : 'The guardrail change could not be applied yet and is waiting in Guardrail requests for a decision.',
      );
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'guardrail_apply_disabled') {
        throw new Error(
          'Guardrail changes are switched off on this deployment (GUARDRAIL_APPLY_ENABLED), so nothing was changed.',
        );
      }
      throw err;
    } finally {
      setGuardrailChangeSubmitting(false);
    }
  };

  const handleRequestIncrease = async (input: BudgetRequestIn) => {
    setRequestSubmitting(true);
    try {
      await api.createBudgetRequest(input);
      setRequestIncreaseKey(null);
      setRequestSubmitting(false);
      setMutationWarning('Budget increase request submitted. Awaiting approval.');
      await load();
    } catch (err) {
      setRequestSubmitting(false);
      throw err;
    }
  };

  const handleRequestGuardrail = async (input: GuardrailRequestIn) => {
    setGuardrailRequestSubmitting(true);
    try {
      await api.createGuardrailRequest(input);
      setRequestGuardrailKey(null);
      setGuardrailRequestSubmitting(false);
      setMutationWarning('Guardrail request submitted. Awaiting approval.');
      await load();
    } catch (err) {
      setGuardrailRequestSubmitting(false);
      throw err;
    }
  };

  useEffect(() => {
    void load();
    // StrictMode double-invokes this in dev; the generation counter makes the first
    // load's result inert rather than letting it race the second.
    return () => {
      generation.current++;
    };
  }, [load]);

  return (
    <div className="app">
      <header className="topbar">
        <div>
          <h1>OpenRouter Manager</h1>
          <p className="tagline">Team keys, spend and budgets</p>
        </div>
        <div className="topbar-actions">
          {data?.teams && data.teams.length > 0 ? (
            <button className="primary" onClick={() => setShowCreateForm(true)} disabled={loading || showCreateForm}>
              Create key
            </button>
          ) : null}
          <button className="refresh" onClick={() => void load(true)} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {session.signOut ? (
            <button className="signout" onClick={session.signOut}>
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      <StatusBar
        health={data?.health ?? null}
        me={data?.me ?? null}
        teams={data?.teams ?? null}
        keyCount={data?.keys.items.length ?? null}
        accountLabel={session.accountLabel}
      />

      {mutationWarning ? (
        <div className="callout callout-warning" role="status">
          <strong>Warning:</strong> {mutationWarning}
          <button onClick={() => setMutationWarning(null)}>Dismiss</button>
        </div>
      ) : null}

      <main>
        {showCreateForm && data?.teams ? (
          <div className="modal-backdrop" onClick={() => setShowCreateForm(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <CreateKeyForm
                teams={data.teams}
                identity={data.me ?? null}
                onSubmit={handleCreateKey}
                onCancel={() => setShowCreateForm(false)}
                submitting={loading}
              />
            </div>
          </div>
        ) : null}

        {createdKey ? (
          <PlaintextModal
            plaintext={createdKey.plaintext}
            keyId={createdKey.key.id}
            keyName={createdKey.key.name}
            warning={createdKey.warning}
            onDismiss={() => setCreatedKey(null)}
          />
        ) : null}

        {requestIncreaseKey ? (
          <div className="modal-backdrop" onClick={() => setRequestIncreaseKey(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <BudgetRequestForm
                keyItem={requestIncreaseKey}
                onSubmit={handleRequestIncrease}
                onCancel={() => setRequestIncreaseKey(null)}
                submitting={requestSubmitting}
              />
            </div>
          </div>
        ) : null}

        {requestGuardrailKey && data?.teams ? (
          <div className="modal-backdrop" onClick={() => setRequestGuardrailKey(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <GuardrailRequestForm
                keyItem={requestGuardrailKey}
                workspaceId={
                  data.teams.find((t) => t.id === requestGuardrailKey.team_id)?.openrouter_workspace_id ?? ''
                }
                onSubmit={handleRequestGuardrail}
                onCancel={() => setRequestGuardrailKey(null)}
                submitting={guardrailRequestSubmitting}
                canAssignPermanent={data.me?.is_platform_admin || data.me?.teams.find((t) => t.team_id === requestGuardrailKey.team_id)?.role === 'lead'}
              />
            </div>
          </div>
        ) : null}

        {changeGuardrailKey && data?.teams ? (
          <div className="modal-backdrop" onClick={() => setChangeGuardrailKey(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <GuardrailChangeForm
                keyItem={changeGuardrailKey}
                workspaceId={
                  data.teams.find((t) => t.id === changeGuardrailKey.team_id)?.openrouter_workspace_id ?? ''
                }
                currentGuardrailId={guardrailNames?.get(changeGuardrailKey.id)?.guardrail_id ?? null}
                onSubmit={handleChangeGuardrail}
                onCancel={() => setChangeGuardrailKey(null)}
                submitting={guardrailChangeSubmitting}
              />
            </div>
          </div>
        ) : null}

        {setupKey ? (
          <div className="modal-backdrop" onClick={() => setSetupKey(null)}>
            <div className="modal plaintext-modal" role="dialog" aria-label="Set up Claude Code" onClick={(e) => e.stopPropagation()}>
              <h2>{setupKey.name}</h2>
              <p className="muted small">
                The script asks for this key&apos;s plaintext, which was shown once when it was
                created. If you no longer have it, create a new key.
              </p>
              <ClaudeCodeSetup keyId={setupKey.id} />
              <div className="form-actions">
                <button type="button" onClick={() => setSetupKey(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {!error && data ? <ModelCatalog models={models} /> : null}

        {error ? (
          <ErrorState error={error} onRetry={() => void load()} />
        ) : data ? (
          <KeyList
            list={data.keys}
            identity={data.me}
            guardrailNames={guardrailNames}
            onUpdate={handleUpdateKey}
            onDisable={handleDisableKey}
            onSetCustomer={handleSetCustomer}
            onRequestIncrease={setRequestIncreaseKey}
            onRequestGuardrail={setRequestGuardrailKey}
            onSetupClaudeCode={setSetupKey}
            onChangeGuardrail={setChangeGuardrailKey}
          />
        ) : (
          <LoadingState />
        )}

        {/*
         * Rendered only for a platform admin, and only once identity is known — every
         * admin endpoint answers 404 to anyone else by design, so mounting this for a
         * normal user would fire requests that can only fail. The server is the
         * real gate; this check just avoids showing a panel that cannot work.
         */}
        {!error && data?.me?.is_platform_admin ? (
          <AdminPanel teams={data.teams ?? []} identity={data.me} onClaimed={() => void load()} />
        ) : null}
      </main>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="loading" role="status" aria-live="polite">
      <p>Loading keys from OpenRouter...</p>
      <div className="skeleton-table" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton-row" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : null;
  const code = error instanceof ApiError ? error.code : null;
  return (
    <div className="error" role="alert">
      <h2>Could not load keys</h2>
      <p className="error-detail">{error.message}</p>
      <p className="muted">
        {status === 0
          ? 'The API did not respond. In development, check the backend is running on port 8000.'
          : status === 401 || status === 403
            ? 'Your session may have expired, or you may not have access to any team.'
            : status === 502 || status === 503
              ? 'OpenRouter is unavailable or rate limiting us. This is an upstream fault, not your data.'
              : 'This is a server-side fault. Retrying is safe: this view performs no writes.'}
      </p>
      <p className="muted small">
        {status !== null ? `HTTP ${status}` : 'network error'}
        {code ? ` - ${code}` : ''}
      </p>
      <button className="refresh" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}
